import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Trophy, Phone, Headphones, ClipboardList, Presentation, DollarSign, Users, Loader2, ArrowDown, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { key: 'dials_made', label: 'Dials', icon: Phone, color: 'text-[#4318FF]' },
  { key: 'contacts_made', label: 'Contacts', icon: Headphones, color: 'text-sky-500' },
  { key: 'appointments_set', label: 'Appts', icon: ClipboardList, color: 'text-amber-500' },
  { key: 'presentations_given', label: 'Presos', icon: Presentation, color: 'text-orange-500' },
  { key: 'sales_made', label: 'Sales', icon: DollarSign, color: 'text-emerald-500' },
  { key: 'recruits_onboarded', label: 'Recruits', icon: Users, color: 'text-violet-500' },
  { key: 'hours_worked', label: 'Hours', icon: Clock, color: 'text-cyan-600', derived: true },
];

// Auto-calculated work time (minutes): 1 dial = 1 min, 1 presentation = 60 min, 1 recruit = 30 min.
const metricVal = (a, key, suffix) => {
  if (key === 'hours_worked') {
    return (a[`dials_made${suffix}`] || 0) * 1 +
           (a[`presentations_given${suffix}`] || 0) * 60 +
           (a[`recruits_onboarded${suffix}`] || 0) * 30;
  }
  return a[`${key}${suffix}`] ?? 0;
};

// Compact duration: 0 → '0', <60 → '45m', else '2h' / '2.6h'.
const fmtDuration = (mins) => {
  if (!mins) return '0';
  if (mins < 60) return `${mins}m`;
  const r = Math.round((mins / 60) * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}h`;
};

const RANK_STYLES = ['bg-amber-400 text-white', 'bg-slate-300 text-slate-700', 'bg-orange-300 text-orange-900'];

export const TrackerLeaderboard = ({ date, refreshKey }) => {
  const { user, getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day'); // 'day' | 'week'
  const [sortKey, setSortKey] = useState('dials_made');

  useEffect(() => {
    axios.get(`${API}/tracker/leaderboard`, { ...getAuthHeader(), params: { date } })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, refreshKey]);

  const rows = useMemo(() => {
    if (!data?.agents) return [];
    const suffix = period === 'day' ? '_day' : '_week';
    return [...data.agents].sort((a, b) =>
      (metricVal(b, sortKey, suffix) - metricVal(a, sortKey, suffix)) ||
      (b[`dials_made${suffix}`] - a[`dials_made${suffix}`])
    );
  }, [data, period, sortKey]);

  const suffix = period === 'day' ? '_day' : '_week';

  return (
    <Card className="bg-white" data-testid="tracker-leaderboard">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#A3AED0]">
              <Trophy className="h-4 w-4 text-amber-400" /> Agency Leaderboard
            </div>
            <div className="mt-0.5 text-[11px] text-[#A3AED0]">
              Everyone's activity, out in the open. {period === 'day' ? 'Today' : `Week of ${data?.week_start || ''}`} — tap a column to rank by it.
            </div>
          </div>
          <div className="flex items-center rounded-lg bg-[#F4F7FE] p-1 self-start">
            {[{ k: 'day', label: 'Today' }, { k: 'week', label: 'This Week' }].map((p) => (
              <button
                key={p.k}
                type="button"
                onClick={() => setPeriod(p.k)}
                data-testid={`leaderboard-period-${p.k}`}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  period === p.k ? 'bg-[#2B3674] text-white shadow-sm' : 'text-[#2B3674] hover:bg-white/60'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#2447D6]" /></div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-[#A3AED0]">No agents found.</div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[697px] -mx-4 sm:mx-0 px-4 sm:px-0" data-testid="leaderboard-scroll-container">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-left text-[10px] uppercase tracking-wide text-[#A3AED0] border-b border-[#E9EDF7] shadow-[0_1px_0_#E9EDF7]">
                  <th className="py-2 pr-2 w-8">#</th>
                  <th className="py-2 pr-3">Agent</th>
                  {METRICS.map((m) => (
                    <th key={m.key} className="py-2 px-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => setSortKey(m.key)}
                        data-testid={`leaderboard-sort-${m.key}`}
                        className={`inline-flex items-center gap-1 mx-auto rounded-md px-2 py-1 transition-colors ${
                          sortKey === m.key ? 'bg-[#2B3674] text-white' : 'hover:bg-[#F4F7FE]'
                        }`}
                      >
                        <m.icon className={`h-3 w-3 ${sortKey === m.key ? 'text-white' : m.color}`} />
                        {m.label}
                        {sortKey === m.key && <ArrowDown className="h-2.5 w-2.5" />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((a, i) => {
                  const isMe = a.user_id === user?.id;
                  return (
                    <tr
                      key={a.user_id}
                      data-testid={`leaderboard-row-${a.user_id}`}
                      className={`border-b border-[#F4F7FE] last:border-0 ${isMe ? 'bg-[#F4F7FE]/70' : ''}`}
                    >
                      <td className="py-2.5 pr-2">
                        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                          RANK_STYLES[i] || 'bg-[#F4F7FE] text-[#A3AED0]'
                        }`}>
                          {i + 1}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {a.profile_picture ? (
                            <img src={a.profile_picture} alt={a.name} className="h-7 w-7 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <span className="h-7 w-7 rounded-full bg-[#2B3674] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                              {(a.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                            </span>
                          )}
                          <span className="font-semibold text-[#2B3674] truncate">{a.name}</span>
                          {isMe && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#2447D6] text-white flex-shrink-0">You</span>}
                        </div>
                      </td>
                      {METRICS.map((m) => {
                        const main = metricVal(a, m.key, suffix);
                        return (
                          <td key={m.key} className="py-2.5 px-1.5 text-center">
                            <div
                              className={`font-['Manrope'] font-extrabold tabular-nums ${m.derived ? 'text-base' : 'text-lg'} ${main > 0 ? 'text-[#2B3674]' : 'text-[#C9D2E8]'}`}
                              title={m.derived ? 'Auto-calculated: 1 dial = 1 min, 1 presentation = 1 hr, 1 recruit = 30 min' : undefined}
                            >
                              {m.derived ? fmtDuration(main) : main}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
